import { runInAction, when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ISegment, Segment } from '@feathr/blackbox';
import { UniversalCreatableSelect } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { objectId } from '@feathr/hooks';

interface IProps {
  disabled?: boolean;
  onChange?: (segment: Segment | undefined) => void;
  value?: string;
}

function ConversionPixelSelect({
  disabled = false,
  onChange,
  value,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Segments } = useStore();

  const segments = Segments.list({
    filters: {
      is_conversion_segment: true,
    },
  });

  async function loadOptions(inputValue: string): Promise<ISegment[]> {
    const filteredSegments = await Segments.list({
      filters: {
        is_conversion_segment: true,
        name__icontains: inputValue,
      },
    });
    await when(() => filteredSegments.isLoaded);

    return filteredSegments.models.map((s) => s.toJS());
  }

  async function handleCreateOption(inputValue: string): Promise<ISegment> {
    const segmentId = objectId();
    const newSegment = Segments.create({
      id: segmentId,
      name: inputValue,
      read_only: true,
      lookback_mode: 'unbounded',
      predicates: [
        {
          attr_type: 'string',
          attr_against: 'seg_id',
          comparison: 'eq',
          kind: 'activity',
          value: segmentId,
        },
      ],
      is_conversion_segment: true,
    });

    runInAction(() => {
      segments.models.push(newSegment);
    });

    if (onChange) {
      onChange(newSegment);
    }

    return newSegment.toJS();
  }

  function handleSelectSingle(option: ISegment | null): void {
    if (onChange) {
      if (option) {
        const segment = Segments.get(option.id);
        onChange(segment);
      } else {
        onChange(undefined);
      }
    }
  }

  function handleClear(): void {
    if (onChange) {
      onChange(undefined);
    }
  }

  const selectedSegment = value ? Segments.get(value) : undefined;

  return (
    <UniversalCreatableSelect<ISegment>
      async={true}
      createOption={handleCreateOption}
      defaultOptions={segments.models.map((s) => s.toJS())}
      disabled={disabled}
      isClearable={true}
      isLoading={segments.isPending}
      label={t('Conversion Pixel')}
      loadOptions={loadOptions}
      name={'conversion-pixel-select'}
      onClear={handleClear}
      onSelectSingle={handleSelectSingle}
      placeholder={t('Select a pixel...')}
      value={selectedSegment?.toJS()}
    />
  );
}

export default observer(ConversionPixelSelect);
